const STORAGE_KEY = "paralegalGatewayRestriction";
const EVENT_NAME = "paralegal:rate-limit";

export class GatewayRequestError extends Error {
  constructor(payload, status) {
    super(payload?.message || `Request blocked (${status})`);
    this.name = "GatewayRequestError";
    this.status = status;
    this.payload = payload || {};
  }
}

export function getRestriction() {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
    if (!value) return null;
    if (!value.locked && value.reset_at && value.reset_at * 1000 <= Date.now()) {
      clearRestriction();
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function setRestriction(payload) {
  const restriction = {
    ...payload,
    locked: payload.code === "ACCOUNT_LOCKED" || payload.locked === true,
    warned: payload.code === "RATE_LIMIT_WARNING" || payload.warned === true,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(restriction));
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: restriction }));
  return restriction;
}

export function clearRestriction() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: null }));
}

export function subscribeToRestriction(listener) {
  const onCustom = (event) => listener(event.detail);
  const onStorage = (event) => {
    if (event.key === STORAGE_KEY) listener(getRestriction());
  };
  window.addEventListener(EVENT_NAME, onCustom);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onCustom);
    window.removeEventListener("storage", onStorage);
  };
}

export async function gatewayFetch(url, options = {}) {
  const response = await fetch(url, options);
  if (![403, 429, 503].includes(response.status)) return response;
  const payload = await response.clone().json().catch(() => ({
    code: response.status === 403 ? "ACCOUNT_LOCKED" : "RATE_LIMIT_SERVICE_UNAVAILABLE",
    message: "This service is temporarily unavailable.",
  }));
  if (payload.code === "RATE_LIMIT_WARNING" || payload.code === "ACCOUNT_LOCKED") {
    setRestriction(payload);
  }
  throw new GatewayRequestError(payload, response.status);
}

export function handleTypesenseGatewayError(error) {
  const candidates = [error?.httpBody, error?.response?.data, error?.message];
  for (const candidate of candidates) {
    try {
      let payload = candidate;
      if (typeof candidate === "string") {
        const start = candidate.indexOf("{");
        const end = candidate.lastIndexOf("}");
        payload = JSON.parse(start >= 0 && end > start ? candidate.slice(start, end + 1) : candidate);
      }
      if (payload?.code === "RATE_LIMIT_WARNING" || payload?.code === "ACCOUNT_LOCKED") {
        setRestriction(payload);
        return new GatewayRequestError(payload, payload.code === "ACCOUNT_LOCKED" ? 403 : 429);
      }
    } catch {
      // Continue and preserve the original Typesense error.
    }
  }
  return error;
}
